/**
 * Halaman Analisis Audit Logs
 * Hanya dapat diakses oleh Super Admin
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import { API_ESA_OFFICE } from "utils";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Pagination from "@mui/material/Pagination";
import Button from "@mui/material/Button";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { logoutUser } from "actions/AuthAction";

/** Aksi standar audit (digabung dengan API agar filter selalu memuat Logout, dll.) */
const AUDIT_ACTIONS_DEFAULT = [
  "login_failed",
  "login_success",
  "logout",
  "sensitive_access",
];

const getActionLabel = (action) => {
  const labels = {
    login_success: "Login Sukses",
    login_failed: "Login Gagal",
    logout: "Logout",
    sensitive_access: "Akses Sensitif",
  };
  return labels[action] || action;
};

const getActionColor = (action) => {
  if (action === "login_success") return "success";
  if (action === "login_failed") return "error";
  if (action === "logout") return "secondary";
  return "info";
};

function AuditLogs() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    action: "",
    date_from: "",
    date_to: "",
  });

  const actionOptions = [
    { value: "", label: "Semua Action" },
    ...actions.map((a) => ({ value: a, label: getActionLabel(a) })),
  ];

  const selectedActionOption =
    actionOptions.find((o) => o.value === (filters.action || "")) ||
    actionOptions[0];

  const fetchActions = async () => {
    try {
      const res = await axios.get(`${API_ESA_OFFICE}audit-logs/actions`, {
        withCredentials: true,
      });
      if (res.data?.success) {
        const fromApi = res.data.actions || [];
        const merged = [...new Set([...AUDIT_ACTIONS_DEFAULT, ...fromApi])];
        merged.sort((a, b) => a.localeCompare(b));
        setActions(merged);
      }
    } catch (err) {
      console.error("Error fetching actions:", err);
      setActions([...AUDIT_ACTIONS_DEFAULT].sort((a, b) => a.localeCompare(b)));
    }
  };

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pagination.limit),
      });
      if (filters.action) params.append("action", filters.action);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const res = await axios.get(
        `${API_ESA_OFFICE}audit-logs?${params.toString()}`,
        { withCredentials: true }
      );

      if (res.data?.success) {
        setLogs(res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          page: res.data.pagination.page,
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        }));
      }
    } catch (err) {
      if (err.response?.status === 403) {
        swal({
          title: "Akses Ditolak",
          text:
            err.response?.data?.message ||
            "Hanya Super Admin yang dapat mengakses halaman ini.",
          icon: "error",
          button: "OK",
        }).then(() => navigate("/dashboards/berita-terkini"));
      } else {
        swal({
          title: "Error",
          text: "Gagal memuat data audit logs.",
          icon: "error",
          button: "OK",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      logoutUser(navigate);
      return;
    }
    if (user.status !== "super admin") {
      swal({
        title: "Akses Ditolak",
        text: "Hanya Super Admin yang dapat mengakses halaman ini.",
        icon: "error",
        button: "OK",
      }).then(() => navigate("/dashboards/berita-terkini"));
      return;
    }
    fetchActions();
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.status === "super admin") {
      fetchLogs(pagination.page);
    }
  }, [pagination.page]);

  const handleFilterApply = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setLoading(true);
    fetchLogs(1);
  };

  const handlePageChange = (e, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const formatDetails = (details) => {
    if (!details) return "-";
    try {
      const parsed = typeof details === "string" ? JSON.parse(details) : details;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        if (parsed.nama != null && String(parsed.nama).trim() !== "") {
          return `Nama: ${parsed.nama}`;
        }
        const keys = Object.keys(parsed);
        if (keys.length === 0) return "-";
      }
      const str = JSON.stringify(parsed);
      return str.length > 120 ? str.substring(0, 120) + "…" : str;
    } catch {
      return String(details);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  if (user?.status !== "super admin") {
    return null;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Analisis Audit Logs
                </MDTypography>
              </MDBox>

              <MDBox p={2}>
                <MDTypography variant="caption" color="text" fontWeight="regular" mb={2} display="block">
                  Filter
                </MDTypography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4} md={3}>
                    <Autocomplete
                      size="small"
                      options={actionOptions}
                      getOptionLabel={(opt) => opt?.label ?? ""}
                      value={selectedActionOption}
                      onChange={(e, newVal) =>
                        setFilters((f) => ({
                          ...f,
                          action: newVal?.value ?? "",
                        }))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Action"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <MDInput
                      type="date"
                      label="Dari Tanggal"
                      value={filters.date_from}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, date_from: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <MDInput
                      type="date"
                      label="Sampai Tanggal"
                      value={filters.date_to}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, date_to: e.target.value }))
                      }
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2}>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<Icon>search</Icon>}
                      onClick={handleFilterApply}
                      fullWidth
                    >
                      Terapkan
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>

              <MDBox px={2} pb={3}>
                <MDTypography
                  variant="button"
                  color="text"
                  fontWeight="bold"
                  display="block"
                  mb={1.5}
                >
                  Tabel Analisis Log
                </MDTypography>
                {loading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress color="info" />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <>
                    <TableContainer sx={{ boxShadow: "none" }}>
                      <Table>
                        <MDBox component="thead">
                          <TableRow>
                            <DataTableHeadCell width="14%">
                              Waktu
                            </DataTableHeadCell>
                            <DataTableHeadCell width="12%">
                              User
                            </DataTableHeadCell>
                            <DataTableHeadCell width="12%">
                              Action
                            </DataTableHeadCell>
                            <DataTableHeadCell width="18%">
                              Route
                            </DataTableHeadCell>
                            <DataTableHeadCell width="8%">
                              Method
                            </DataTableHeadCell>
                            <DataTableHeadCell width="12%">
                              IP Address
                            </DataTableHeadCell>
                            <DataTableHeadCell width="24%">
                              Detail
                            </DataTableHeadCell>
                          </TableRow>
                        </MDBox>
                        <TableBody>
                          {logs.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={7}
                                align="center"
                                sx={{ py: 4 }}
                              >
                                <MDTypography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Tidak ada data audit logs
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            logs.map((row, idx) => (
                              <TableRow key={row.id} hover>
                                <DataTableBodyCell noBorder={idx === logs.length - 1}>
                                  <MDBox component="span" sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}>
                                    {formatDate(row.created_at)}
                                  </MDBox>
                                </DataTableBodyCell>
                                <DataTableBodyCell
                                  noBorder={idx === logs.length - 1}
                                >
                                  {row.user_nama || "-"}
                                </DataTableBodyCell>
                                <DataTableBodyCell
                                  noBorder={idx === logs.length - 1}
                                >
                                  <Chip
                                    label={getActionLabel(row.action)}
                                    color={getActionColor(row.action)}
                                    size="small"
                                    variant="filled"
                                  />
                                </DataTableBodyCell>
                                <DataTableBodyCell noBorder={idx === logs.length - 1}>
                                  <MDBox component="span" sx={{ fontSize: "0.75rem", wordBreak: "break-word", maxWidth: 200, display: "block" }}>
                                    {row.route || "-"}
                                  </MDBox>
                                </DataTableBodyCell>
                                <DataTableBodyCell
                                  noBorder={idx === logs.length - 1}
                                >
                                  {row.method || "-"}
                                </DataTableBodyCell>
                                <DataTableBodyCell noBorder={idx === logs.length - 1}>
                                  <MDBox component="span" sx={{ fontSize: "0.75rem" }}>
                                    {row.ip_address || "-"}
                                  </MDBox>
                                </DataTableBodyCell>
                                <DataTableBodyCell noBorder={idx === logs.length - 1}>
                                  <MDBox component="span" sx={{ fontSize: "0.7rem", wordBreak: "break-all", maxWidth: 220, display: "block" }}>
                                    {formatDetails(row.details)}
                                  </MDBox>
                                </DataTableBodyCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {pagination.totalPages > 1 && (
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={3}
                        flexWrap="wrap"
                        gap={1}
                      >
                        <MDTypography variant="button" color="secondary">
                          Total: {pagination.total} log
                        </MDTypography>
                        <Pagination
                          count={pagination.totalPages}
                          page={pagination.page}
                          onChange={handlePageChange}
                          color="info"
                          size="small"
                          showFirstButton
                          showLastButton
                        />
                      </MDBox>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AuditLogs;
